/**
 * The vocabulary the world is written in: room, prop, ground and wall types,
 * the footprint rules, and a deterministic hash. Kept apart from the world
 * data itself so the town builder and the interiors can both import it
 * without a cycle.
 *
 * Coordinates are tiles. x grows down-right on screen, y down-left. Tile
 * (i, j) covers [i, i+1) × [j, j+1); a blob standing on it is at (i+.5, j+.5).
 */

import type { Look } from './cosmetics.ts'
import type { Emote } from './protocol.ts'

export type RoomId = 'town' | 'cafe' | 'arcade' | 'hatshop' | 'teahouse' | 'lotus'

export const GROUNDS = [
  'grass', 'forest', 'moss', 'dirt', 'stone', 'cobble', 'gravel', 'asphalt', 'sidewalk', 'planks', 'bridge',
  'tiles', 'tatami', 'neon', 'sand', 'wetsand', 'carpet', 'canal', 'sea',
] as const
export type Ground = (typeof GROUNDS)[number]
/** Grounds nobody can walk on (unless a later zone, like a bridge, paves over them). */
export const WATER = new Set<Ground>(['canal', 'sea'])

export type PropKind =
  // nature
  | 'tree' | 'pine' | 'palm' | 'willow' | 'sakura' | 'bamboo' | 'bush' | 'flowers' | 'hedge' | 'rock' | 'hay' | 'crop'
  | 'reeds' | 'mushroom' | 'fern'
  // clutter
  | 'crate' | 'barrel' | 'pot' | 'mailbox' | 'bin' | 'bicycle' | 'fence' | 'stall' | 'cart' | 'well' | 'birdbath'
  | 'bollard' | 'aboard' | 'festoon' | 'laundry' | 'decal'
  // town furniture
  | 'bench' | 'lamppost' | 'fountain' | 'signpost' | 'statue' | 'gazebo' | 'windmill' | 'tramstop' | 'gate'
  | 'stonelantern' | 'lanternpost' | 'streetlamp' | 'trafficlight' | 'car' | 'hydrant' | 'boat'
  // water and beach
  | 'pond' | 'water' | 'stone' | 'picnic' | 'parasol' | 'towel' | 'sandcastle' | 'campfire' | 'stump' | 'crosswalk'
  | 'building'
  // indoors
  | 'table' | 'chair' | 'sofa' | 'counter' | 'shelf' | 'plant' | 'lamp' | 'rug' | 'arcade' | 'beanbag'
  | 'djbooth' | 'vending' | 'piano' | 'dancefloor' | 'hatstand' | 'lowtable' | 'cushion' | 'stool' | 'screen'

export type BuildingStyle = 'cottage' | 'canal' | 'teahouse' | 'pagoda' | 'tower' | 'shop' | 'shack' | 'lighthouse'

export interface Prop {
  kind: PropKind
  x: number
  y: number
  /** Footprint override (rugs, ponds, counters, buildings, gates). Otherwise the kind's default. */
  w?: number
  d?: number
  /** Long props: run along y instead of x. Buildings: 0 = door on the +y face, 1 = on the +x face. */
  rot?: 0 | 1
  color?: string
  /** Seats: which way the sitter looks (+1 = towards screen right). */
  face?: 1 | -1
  /** Signposts, neon, building signs, gate plaques; hat stands: the hat id on show. */
  text?: string
  /** Buildings. */
  roof?: string
  door?: number
  style?: BuildingStyle
  /** Buildings: wall height in tiles (towers are tall). */
  h?: number
  /** Buildings you can walk into: the interior behind the door. */
  enter?: RoomId
  /**
   * Free-form look switch: gates ('torii' | 'paifang' | 'neon' | 'arch'), crops ('wheat' | 'cabbage' |
   * 'pumpkin' | 'sunflower'), decals ('leaves' | 'petals' | 'puddle' | 'crack' | 'manhole' | 'clover' |
   * 'shells' | 'seaweed' | 'moss' | 'roadline' | 'roadline-y'), pots ('flower' | 'tree' | 'bonsai').
   */
  variant?: string
}

export type WallKind = 'window' | 'painting' | 'neon' | 'board' | 'clock' | 'door' | 'mirror' | 'shoji' | 'moon' | 'scroll'
export interface WallItem {
  kind: WallKind
  wall: 'back' | 'left'
  /** Position along the wall (x for the back wall, y for the left one), and length. */
  at: number
  len: number
  color?: string
  text?: string
}

export interface Portal {
  /** The tile you step on to leave. */
  x: number
  y: number
  to: RoomId
  /** Where you appear when arriving here from `to`: the tile just outside the door. */
  out: [number, number]
  label: string
}

export interface Critter {
  kind: 'cat' | 'duck' | 'pigeon' | 'crab' | 'butterfly' | 'boat' | 'koi' | 'car' | 'tram' | 'gull'
  color?: string
  loop: Array<[number, number]>
  speed: number
}

/** A named place, for the map, fast travel and the passport. */
export interface Place { id: string; name: string; blurb: string; x: number; y: number; accent: string; r?: number }

/** Tiles that do something when you stand on them (client-side only). */
export interface Spot { x: number; y: number; action: 'wardrobe' | 'tram' }

export interface RoomDef {
  id: RoomId
  name: string
  blurb: string
  w: number
  d: number
  outdoor: boolean
  ground: Ground
  /** Rectangles of a different ground, painted in order (later wins). */
  zones: Array<[number, number, number, number, Ground]>
  accent: string
  wall?: { color: string; trim: string }
  spawn: [number, number]
  props: Prop[]
  walls: WallItem[]
  portals: Portal[]
  critters: Critter[]
  places: Place[]
  spots: Spot[]
  /** Hard cap per instance, and where the census starts opening another. */
  cap: number
  softCap: number
  /** Ambient effects drawn by the client over rectangles of the room (petals, fireflies…). */
  ambience?: Ambience[]
}

export type AmbienceKind = 'petals' | 'fireflies' | 'leaves' | 'gulls' | 'sparkles'
export interface Ambience { kind: AmbienceKind; x: number; y: number; w: number; d: number }

interface KindInfo { w: number; d: number; solid: boolean; seat?: boolean; flat?: boolean }

export const KINDS: Record<PropKind, KindInfo> = {
  tree: { w: 1, d: 1, solid: true },
  pine: { w: 1, d: 1, solid: true },
  palm: { w: 1, d: 1, solid: true },
  willow: { w: 1, d: 1, solid: true },
  sakura: { w: 1, d: 1, solid: true },
  bamboo: { w: 1, d: 1, solid: true },
  bush: { w: 1, d: 1, solid: true },
  flowers: { w: 1, d: 1, solid: true },
  hedge: { w: 1, d: 1, solid: true },
  rock: { w: 1, d: 1, solid: true },
  hay: { w: 1, d: 1, solid: true },
  crop: { w: 1, d: 1, solid: true },
  reeds: { w: 1, d: 1, solid: true },
  mushroom: { w: 1, d: 1, solid: false, flat: true },
  fern: { w: 1, d: 1, solid: false, flat: true },
  crate: { w: 1, d: 1, solid: true },
  barrel: { w: 1, d: 1, solid: true },
  pot: { w: 1, d: 1, solid: true },
  mailbox: { w: 1, d: 1, solid: true },
  bin: { w: 1, d: 1, solid: true },
  bicycle: { w: 1, d: 1, solid: true },
  fence: { w: 1, d: 1, solid: true },
  stall: { w: 2, d: 1, solid: true },
  cart: { w: 2, d: 1, solid: true },
  well: { w: 1, d: 1, solid: true },
  birdbath: { w: 1, d: 1, solid: true },
  bollard: { w: 1, d: 1, solid: true },
  aboard: { w: 1, d: 1, solid: true },
  // Strung between two posts over a path; you walk under them.
  festoon: { w: 4, d: 1, solid: false },
  laundry: { w: 3, d: 1, solid: false },
  decal: { w: 1, d: 1, solid: false, flat: true },
  bench: { w: 2, d: 1, solid: false, seat: true },
  lamppost: { w: 1, d: 1, solid: true },
  fountain: { w: 3, d: 3, solid: true },
  signpost: { w: 1, d: 1, solid: true },
  statue: { w: 1, d: 1, solid: true },
  gazebo: { w: 3, d: 3, solid: false },
  windmill: { w: 3, d: 3, solid: true },
  tramstop: { w: 2, d: 1, solid: true },
  // Gates are drawn over a path; you walk under them.
  gate: { w: 3, d: 1, solid: false },
  stonelantern: { w: 1, d: 1, solid: true },
  lanternpost: { w: 1, d: 1, solid: true },
  streetlamp: { w: 1, d: 1, solid: true },
  trafficlight: { w: 1, d: 1, solid: true },
  car: { w: 2, d: 1, solid: true },
  hydrant: { w: 1, d: 1, solid: true },
  boat: { w: 2, d: 1, solid: true },
  pond: { w: 4, d: 3, solid: true, flat: true },
  water: { w: 4, d: 2, solid: true, flat: true },
  stone: { w: 1, d: 1, solid: false, flat: true },
  picnic: { w: 2, d: 2, solid: false, seat: true, flat: true },
  parasol: { w: 1, d: 1, solid: true },
  towel: { w: 1, d: 2, solid: false, seat: true, flat: true },
  sandcastle: { w: 1, d: 1, solid: true },
  campfire: { w: 1, d: 1, solid: true },
  stump: { w: 1, d: 1, solid: false, seat: true },
  crosswalk: { w: 3, d: 2, solid: false, flat: true },
  building: { w: 5, d: 4, solid: true },
  table: { w: 1, d: 1, solid: true },
  chair: { w: 1, d: 1, solid: false, seat: true },
  sofa: { w: 2, d: 1, solid: false, seat: true },
  counter: { w: 4, d: 1, solid: true },
  shelf: { w: 2, d: 1, solid: true },
  plant: { w: 1, d: 1, solid: true },
  lamp: { w: 1, d: 1, solid: true },
  rug: { w: 3, d: 2, solid: false, flat: true },
  arcade: { w: 1, d: 1, solid: true },
  beanbag: { w: 1, d: 1, solid: false, seat: true },
  djbooth: { w: 2, d: 1, solid: true },
  vending: { w: 1, d: 1, solid: true },
  piano: { w: 2, d: 1, solid: true },
  dancefloor: { w: 4, d: 3, solid: false, flat: true },
  hatstand: { w: 1, d: 1, solid: true },
  lowtable: { w: 1, d: 1, solid: true },
  cushion: { w: 1, d: 1, solid: false, seat: true },
  stool: { w: 1, d: 1, solid: false, seat: true },
  screen: { w: 2, d: 1, solid: true },
}

/** Footprint of a placed prop, after overrides and rotation. Buildings and gates never swap. */
export function footprint(p: Prop): { w: number; d: number } {
  const k = KINDS[p.kind]
  const w = p.w ?? k.w, d = p.d ?? k.d
  return p.rot && p.kind !== 'building' && p.kind !== 'gate' ? { w: d, d: w } : { w, d }
}

/** Where a building's door is: the walkable tile just in front of it. */
export function doorOf(b: Prop): { x: number; y: number } {
  const { w, d } = footprint(b)
  const off = b.door ?? Math.floor((b.rot ? d : w) / 2)
  return b.rot ? { x: b.x + w, y: b.y + off } : { x: b.x + off, y: b.y + d }
}

/** Deterministic 0..1 noise; the same on every machine. */
export function hash01(a: number, b: number, c = 0): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

// ─── Townsfolk ──────────────────────────────────────────────────────────────

export interface Stop {
  x: number
  y: number
  /** Seconds to linger once there. */
  wait: number
  emote?: Emote
  nap?: boolean
  /** Say something on arrival: an index into `lines`, or -1 for a random line. */
  say?: number
}

/** A scripted NPC: a fixed loop of stops. No AI; see shared/npcs.ts. */
export interface Npc {
  id: string
  name: string
  role: string
  look: Look
  /** Tiles per second (players walk at 3.2). */
  speed: number
  stops: Stop[]
  lines: string[]
}
